import React, { ReactElement, useEffect, useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchFOAMfratInstructors,
  fetchAllInstructorSessions,
  fetchInstructorUsers,
  selectCurrentUserInstructorLink,
  selectFOAMfratInstructorsStatus,
  selectInstructorSessionsStatus,
  selectInstructorUsersStatus,
} from 'src/v3/content-pipeline/instructor.slice'
import {
  fetchEvents,
  selectEventsStatus,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { selectRoles, selectUserID } from 'src/v3/settings/settings.slice'
import SchedulingTab from './scheduling-tab'
import InstructorViewTab from './instructor-view-tab'

type DashboardTab = 'scheduling' | 'instructor-view'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let roles = useAppSelector(selectRoles)
  let userID = useAppSelector(selectUserID)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let instructorsStatus = useAppSelector(selectFOAMfratInstructorsStatus)
  let sessionsStatus = useAppSelector(selectInstructorSessionsStatus)
  let instructorUsersStatus = useAppSelector(selectInstructorUsersStatus)
  let isComplianceAdmin = roles.includes('content-compliance')
  let isInstructor = roles.includes('content-instructor')
  let myInstructorLink = useAppSelector(selectCurrentUserInstructorLink(userID))

  let [tab, setTab] = useState<DashboardTab>(
    isComplianceAdmin ? 'scheduling' : 'instructor-view',
  )

  useEffect(() => {
    if (instructorsStatus === 'uninitialized') {
      dispatch(fetchFOAMfratInstructors())
    }
  }, [instructorsStatus, dispatch])

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus, dispatch])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchAllInstructorSessions())
    }
  }, [sessionsStatus, dispatch])

  useEffect(() => {
    if (instructorUsersStatus === 'uninitialized') {
      dispatch(fetchInstructorUsers())
    }
  }, [instructorUsersStatus, dispatch])

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
        Instructor dashboard
      </Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
        Live class scheduling, instructor sentiment, and system account links.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        aria-label="Instructor dashboard navigation"
      >
        {isComplianceAdmin && (
          <Tab
            label="Scheduling"
            value="scheduling"
            id="tab-scheduling"
            aria-controls="tabpanel-scheduling"
          />
        )}
        {(isComplianceAdmin || isInstructor) && (
          <Tab
            label="Instructor view"
            value="instructor-view"
            id="tab-instructor-view"
            aria-controls="tabpanel-instructor-view"
          />
        )}
      </Tabs>

      {tab === 'scheduling' && isComplianceAdmin && (
        <Box
          role="tabpanel"
          id="tabpanel-scheduling"
          aria-labelledby="tab-scheduling"
        >
          <SchedulingTab />
        </Box>
      )}

      {tab === 'instructor-view' && (isComplianceAdmin || isInstructor) && (
        <Box
          role="tabpanel"
          id="tabpanel-instructor-view"
          aria-labelledby="tab-instructor-view"
        >
          <InstructorViewTab
            isComplianceAdmin={isComplianceAdmin}
            myInstructorLink={myInstructorLink}
          />
        </Box>
      )}
    </Box>
  )
}
